import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  formatBrandMoney,
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandLocale,
  getBrandDatePickerLocale,
} from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Users, DollarSign, Building, MapPin, Phone, Mail, FileText, Clock, List, LayoutList, Calendar, Send, MessageSquare, AlertOctagon, Tag } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
// Tables-mobile.css - DELETED (not needed)

registerLocale('fr', fr);
registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

/** Colonnes de données configurables — cases à cocher + Actions toujours visibles */
const RELANCE_TABLE_COLUMNS_MAX = 8;
const RELANCE_TABLE_VISIBLE_COLS_STORAGE = 'facturation.relances.tableVisibleCols';
const RELANCE_TABLE_COLUMN_KEYS = [
  'reminder_number',
  'reminder_date',
  'client_name',
  'invoice_number',
  'amount_due',
  'days_overdue',
  'period',
  'is_automatic',
  'type',
  'reminder_type',
  'status',
];
const RELANCE_TABLE_COLUMN_LABEL_KEYS = {
  reminder_number: 'col_reminder_number',
  reminder_date: 'col_date',
  client_name: 'col_client',
  invoice_number: 'col_invoice',
  amount_due: 'col_amount',
  days_overdue: 'col_overdue',
  period: 'col_period',
  is_automatic: 'col_mode',
  type: 'col_reminder_type',
  reminder_type: 'col_channel',
  status: 'col_status',
};
const DEFAULT_RELANCE_TABLE_VISIBLE_COLS = RELANCE_TABLE_COLUMN_KEYS.slice(
  0,
  RELANCE_TABLE_COLUMNS_MAX,
);

function readRelanceTableVisibleColsFromStorage() {
  const allowed = new Set(RELANCE_TABLE_COLUMN_KEYS);
  const order = RELANCE_TABLE_COLUMN_KEYS;
  try {
    const raw = localStorage.getItem(RELANCE_TABLE_VISIBLE_COLS_STORAGE);
    if (!raw) return [...DEFAULT_RELANCE_TABLE_VISIBLE_COLS];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [...DEFAULT_RELANCE_TABLE_VISIBLE_COLS];
    const uniq = [...new Set(arr.filter((k) => allowed.has(k)))];
    const sorted = order.filter((k) => uniq.includes(k));
    return sorted.length > 0
      ? sorted.slice(0, RELANCE_TABLE_COLUMNS_MAX)
      : [...DEFAULT_RELANCE_TABLE_VISIBLE_COLS];
  } catch {
    return [...DEFAULT_RELANCE_TABLE_VISIBLE_COLS];
  }
}

/** Inline edit cell — MUST stay outside RelancesPage. */
function RelanceEditableCell({
  recordId,
  field,
  value,
  className = 'text-white font-semibold',
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  clickToEditTitle,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSaveRef.current = true;
            onSave(recordId, field, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0 min-w-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }

  const disp = [null, undefined, ''].includes(value) ? '-' : String(value);

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(recordId, field, value);
        }
      }}
      className={`${className} cursor-pointer`}
      title={clickToEditTitle}
    >
      {disp}
    </span>
  );
}

const RelancesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.relancesPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const clickToEditTitle = p('click_to_edit');

  const relanceTableColumnOptions = useMemo(
    () => RELANCE_TABLE_COLUMN_KEYS.map((key) => ({
      key,
      label: p(RELANCE_TABLE_COLUMN_LABEL_KEYS[key]),
    })),
    [t],
  );

  const statusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'sent', label: p('status_sent'), icon: Send, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'responded', label: p('status_responded'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'no_response', label: p('status_no_response'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [t]);

  const typeOptions = useMemo(() => [
    { value: 'email', label: p('channel_email'), icon: Mail },
    { value: 'phone', label: p('channel_phone'), icon: Phone },
    { value: 'letter', label: p('channel_letter'), icon: FileText },
    { value: 'sms', label: p('channel_sms'), icon: MessageSquare },
  ], [t]);

  const getStatusLabel = useCallback((value) => {
    const opt = statusOptions.find((s) => s.value === value);
    return opt ? opt.label : value;
  }, [statusOptions]);

  const getRelanceTypeLabel = useCallback((type) => {
    const labels = {
      reminder: p('relance_type_reminder'),
      formal_notice: p('relance_type_formal'),
      final_notice: p('relance_type_final'),
      legal: p('relance_type_legal'),
      friendly: p('relance_type_friendly'),
      urgent: p('relance_type_urgent'),
    };
    return labels[type] || type;
  }, [t]);

  const exportHeaders = useMemo(() => [
    { key: 'reminder_number', label: p('col_reminder_number') },
    { key: 'reminder_date', label: p('col_date') },
    { key: 'client_name', label: p('col_client') },
    { key: 'invoice_number', label: p('col_invoice') },
    { key: 'amount_due', label: p('col_amount') },
    { key: 'days_overdue', label: p('export_col_overdue_days') },
    { key: 'status', label: p('col_status') },
  ], [t]);

  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const { settings } = useSettings();
  const [searchTerm, setSearchTerm] = useState('');
  const [relances, setRelances] = useState([]);
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);

  const [showViewModal, setShowViewModal] = useState(false);
  useFacturationModalBodyLock(showViewModal);
  const [viewingRecord, setViewingRecord] = useState(null);

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [visibleRelanceTableCols, setVisibleRelanceTableCols] = useState(
    readRelanceTableVisibleColsFromStorage,
  );
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_reminders');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadRelances(); 
    loadUnpaidInvoices();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadRelances = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/relances');
      setRelances(response?.data || response || []);
    } catch (error) {
      console.error('Error loading relances:', error);
      setRelances([]);
    }
  };

  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    if (field === 'amount_due') {
      setEditValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
    } else {
      setEditValue(currentValue ?? '');
    }
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, rawValue) => {
    const record = relances.find((r) => r.id === recordId);
    if (!record) {
      cancelEditing();
      return;
    }

    if (field === 'amount_due') {
      const raw = String(rawValue ?? '')
        .replace(/\s/g, '')
        .replace(/\u00a0/g, '')
        .replace(',', '.');
      const n = parseFloat(raw);
      if (Number.isNaN(n)) {
        cancelEditing();
        return;
      }
      const orig = parseFloat(record.amount_due);
      const origN = Number.isNaN(orig) ? 0 : orig;
      if (n === origN) {
        cancelEditing();
        return;
      }
      try {
        await localApiAdapter.put(`/facturation/relances/${recordId}`, { amount_due: n });
        await loadRelances();
        cancelEditing();
        setViewingRecord((prev) => (prev && prev.id === recordId ? { ...prev, amount_due: n } : prev));
      } catch (error) {
        console.error('Error saving inline edit:', error);
        cancelEditing();
      }
      return;
    }

    const norm = (v) => String(v ?? '').trim();
    const newVal = norm(rawValue);
    const originalVal = norm(record[field]);
    if (newVal === originalVal) {
      cancelEditing();
      return;
    }
    if (field === 'reminder_number' && !newVal) {
      cancelEditing();
      return;
    }

    try {
      await localApiAdapter.put(`/facturation/relances/${recordId}`, { [field]: newVal });
      await loadRelances();
      cancelEditing();
      setViewingRecord((prev) => (prev && prev.id === recordId ? { ...prev, [field]: newVal } : prev));
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
    }
  };

  const calculateDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const loadUnpaidInvoices = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/impayes-creances');
      const rows = Array.isArray(response) ? response : (response?.data || []);
      const openStatuses = new Set(['ouvert', 'partiel', 'litige']);

      let invoices = rows
        .filter((d) => openStatuses.has(d.status))
        .map((d) => ({
          number: d.invoice_number,
          invoice_number: d.invoice_number,
          invoice_id: d.invoice_id,
          date: d.invoice_date || null,
          due_date: d.due_date,
          client_id: d.client_id,
          client_name: d.client_name,
          client_email: d.client_email || '',
          client_phone: d.client_phone || '',
          amount_due: d.amount_due,
          days_overdue: d.days_overdue ?? calculateDaysOverdue(d.due_date),
        }));

      if (invoices.length === 0) {
        const archivesResp = await localApiAdapter.get('/facturation/archives?type=invoice');
        const docs = Array.isArray(archivesResp) ? archivesResp : (archivesResp?.data || []);
        invoices = docs
          .filter((doc) => {
            const ps = String(doc.paymentStatus || doc.payment_status || doc.status || '').toLowerCase();
            const overdue = calculateDaysOverdue(doc.dueDate || doc.due_date) > 0;
            return ['pending', 'partial', 'overdue', 'impayé', 'impaye', 'partiel'].includes(ps) || overdue;
          })
          .map((doc) => ({
            number: doc.number,
            invoice_number: doc.number,
            invoice_id: doc.id,
            date: doc.date,
            due_date: doc.dueDate || doc.due_date,
            client_id: doc.client_id || doc.clientId,
            client_name: doc.client || doc.client_name,
            client_email: doc.client_email || '',
            client_phone: doc.client_phone || '',
            amount_due: doc.amount ?? doc.total ?? 0,
            days_overdue: calculateDaysOverdue(doc.dueDate || doc.due_date),
          }));
      }

      setUnpaidInvoices(invoices);
    } catch (error) {
      console.error('Error loading unpaid invoices:', error);
      setUnpaidInvoices([]);
    }
  };

  const stats = {
    total: relances.length,
    sent: relances.filter(r => r.status === 'sent').length,
    pending: relances.filter(r => r.status === 'pending').length,
    overdue: relances.filter(r => parseInt(r.days_overdue || 0, 10) > 30).length,
    automatic: relances.filter(r => r.is_automatic).length,
    manual: relances.filter(r => !r.is_automatic).length,
    unpaidInvoices: unpaidInvoices.length,
  };

  // ─── Export/Import state ────────────────────────────────────────────────────
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);

  // ─── Export handlers ────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    try {
      const headers = exportHeaders.map((h) => h.label);
      const rows = filteredRelances.map(r => [
        r.reminder_number || '',
        r.reminder_date || '',
        r.client_name || '',
        r.invoice_number || '',
        formatBrandMoney(parseFloat(r.amount_due || 0)),
        p('days_suffix', { count: r.days_overdue || 0 }),
        getStatusLabel(r.status),
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `relances-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV export error:', error);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const XLSX = await import('xlsx');
      const wsData = [
        exportHeaders.map((h) => h.label),
        ...filteredRelances.map(r => [
          r.reminder_number || '',
          r.reminder_date || '',
          r.client_name || '',
          r.invoice_number || '',
          parseFloat(r.amount_due || 0),
          r.days_overdue || 0,
          getStatusLabel(r.status),
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, p('export_client_title'));
      XLSX.writeFile(wb, `relances-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: filteredRelances,
        columns: [
          { label: p('col_reminder_number'), value: (r) => r.reminder_number },
          { label: p('col_date'), value: (r) => r.reminder_date },
          { label: p('col_client'), value: (r) => r.client_name },
          { label: p('col_invoice'), value: (r) => r.invoice_number },
          {
            label: p('col_amount'),
            align: 'right',
            value: (r) => formatBrandMoney(parseFloat(r.amount_due || 0)),
          },
          { label: p('col_overdue'), value: (r) => p('days_suffix', { count: r.days_overdue || 0 }) },
          { label: p('col_status'), value: (r) => getStatusLabel(r.status) },
        ],
        listExportTitle: p('export_list_title'),
        clientTitle: p('export_client_title'),
        printDocumentTitle: `${p('export_client_title')} - ${new Date().toISOString().split('T')[0]}`,
      });
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportJSON = () => {
    try {
      const dataStr = JSON.stringify(filteredRelances, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `relances-${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('JSON export error:', error);
    }
  };

  const handleExportXML = () => {
    try {
      const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<relances>
${filteredRelances.map(r => `  <relance>
    <numero>${r.reminder_number || ''}</numero>
    <date>${r.reminder_date || ''}</date>
    <client>${r.client_name || ''}</client>
    <facture>${r.invoice_number || ''}</facture>
    <montant>${parseFloat(r.amount_due || 0)}</montant>
    <retard>${r.days_overdue || 0}</retard>
    <statut>${r.status}</statut>
  </relance>`).join('\n')}
</relances>`;
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `relances-${new Date().toISOString().split('T')[0]}.xml`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('XML export error:', error);
    }
  };

  const handleExportHTML = () => {
    try {
      const htmlContent = `<!DOCTYPE html>
<html lang="${dateLocale.startsWith('ar') ? 'ar' : 'en'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${p('title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #333; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #4CAF50; color: white; }
    tr:nth-child(even) { background-color: #f2f2f2; }
  </style>
</head>
<body>
  <h1>${p('title')}</h1>
  <p>${p('export_date_label')}: ${new Date().toLocaleString(dateLocale)}</p>
  <table>
    <thead>
      <tr>
        <th>${p('col_reminder_number')}</th>
        <th>${p('col_date')}</th>
        <th>${p('col_client')}</th>
        <th>${p('col_invoice')}</th>
        <th>${p('col_amount')}</th>
        <th>${p('col_overdue')}</th>
        <th>${p('col_status')}</th>
      </tr>
    </thead>
    <tbody>
${filteredRelances.map(r => `      <tr>
        <td>${r.reminder_number || ''}</td>
        <td>${r.reminder_date || ''}</td>
        <td>${r.client_name || ''}</td>
        <td>${r.invoice_number || ''}</td>
        <td>${formatBrandMoney(parseFloat(r.amount_due || 0))}</td>
        <td>${p('days_suffix', { count: r.days_overdue || 0 })}</td>
        <td>${getStatusLabel(r.status)}</td>
      </tr>`).join('\n')}
    </tbody>
  </table>
</body>
</html>`;
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `relances-${new Date().toISOString().split('T')[0]}.html`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('HTML export error:', error);
    }
  };

  const handleExportTXT = () => {
    try {
      const txtContent = `${p('export_list_title')}
${p('export_date_label')}: ${new Date().toLocaleString(dateLocale)}
${'='.repeat(80)}

${filteredRelances.map(r => `${p('col_reminder_number')}: ${r.reminder_number || ''}
${p('col_date')}: ${r.reminder_date || ''}
${p('col_client')}: ${r.client_name || ''}
${p('col_invoice')}: ${r.invoice_number || ''}
${p('col_amount')}: ${formatBrandMoney(parseFloat(r.amount_due || 0))}
${p('col_overdue')}: ${p('days_suffix', { count: r.days_overdue || 0 })}
${p('col_status')}: ${getStatusLabel(r.status)}
${'-'.repeat(80)}`).join('\n')}`;
      const blob = new Blob([txtContent], { type: 'text/plain' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `relances-${new Date().toISOString().split('T')[0]}.txt`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('TXT export error:', error);
    }
  };

  // ─── Import handlers ────────────────────────────────────────────────────────
  const handleImportCSV = () => {
    const headers = exportHeaders;
    const schema = {
      reminder_number: (value) => value && value.toString().trim() !== '',
      reminder_date: (value) => value && !isNaN(Date.parse(value)),
      amount_due: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0,
      status: (value) => ['pending', 'sent', 'responded', 'no_response'].includes(value) || !value
    };
    triggerFileInput(async (file) => {
      try {
        const importResults = await importFromFile(file, headers, schema);
        if (importResults.success) {
          alert(p('import_success', { count: importResults.data.length }));
        } else {
          alert(p('import_errors', { count: importResults.errors.length }));
        }
      } catch (error) {
        alert(p('import_file_error'));
      }
    });
  };

  const handleImportExcel = () => handleImportCSV();
  const handleImportJSON = () => handleImportCSV();
  const handleImportXML = () => handleImportCSV();
  const handleImportTXT = () => handleImportCSV();

  const mainTabs = [
    { id: 'all_reminders', name: p('tab_general'), icon: LayoutGrid },
    { id: 'automatiques', name: p('tab_automatic'), icon: RefreshCcw },
    { id: 'manuelles', name: p('tab_manual'), icon: Users },
    { id: 'archives', name: p('tab_archives'), icon: FileText },
    { id: 'par_type', name: p('tab_by_type'), icon: MessageSquare },
    { id: 'par_statut', name: p('tab_by_status'), icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'sent', name: p('filter_sent'), icon: Send },
    { id: 'responded', name: p('filter_responded'), icon: CheckCircle },
    { id: 'critical', name: p('filter_critical'), icon: AlertOctagon }
  ];

  const relancesDistribution = {
    all_reminders: {
      title: p('overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'sent', name: p('dist_sent'), count: stats.sent, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'responded', name: p('dist_responded'), count: relances.filter(r => r.status === 'responded').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'overdue_30', name: p('dist_overdue_30'), count: stats.overdue, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    },
    automatiques: {
      title: p('auto_title'),
      data: [
        { filterKey: 'scheduled', name: p('dist_scheduled'), count: relances.filter(r => r.is_automatic && r.status === 'pending').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'sent_auto', name: p('dist_sent_auto'), count: relances.filter(r => r.is_automatic && r.status === 'sent').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'failed', name: p('dist_failed'), count: relances.filter(r => r.is_automatic && r.status === 'failed').length, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'total_auto', name: p('dist_total_auto'), count: relances.filter(r => r.is_automatic).length, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    manuelles: {
      title: p('manual_title'),
      data: [
        { filterKey: 'to_send', name: p('dist_to_send'), count: relances.filter(r => !r.is_automatic && r.status === 'pending').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'sent_manual', name: p('dist_sent_manual'), count: relances.filter(r => !r.is_automatic && r.status === 'sent').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'responded_manual', name: p('dist_responded_manual'), count: relances.filter(r => !r.is_automatic && r.status === 'responded').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'total_manual', name: p('dist_total_manual'), count: relances.filter(r => !r.is_automatic).length, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
      ]
    },
    archives: {
      title: p('archives_title'),
      data: [
        { filterKey: 'this_week', name: p('dist_this_week'), count: relances.filter(r => {
          const date = new Date(r.reminder_date);
          const today = new Date();
          const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
          return date >= weekAgo;
        }).length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'this_month', name: p('dist_this_month'), count: relances.filter(r => {
          const date = new Date(r.reminder_date);
          return date.getMonth() === new Date().getMonth();
        }).length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'this_year', name: p('dist_this_year'), count: relances.filter(r => {
          const date = new Date(r.reminder_date);
          return date.getFullYear() === new Date().getFullYear();
        }).length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'total_archived', name: p('dist_total_archived'), count: relances.length, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
      ]
    },
    par_type: {
      title: p('by_type_title'),
      data: [
        { filterKey: 'email', name: p('dist_email'), count: relances.filter(r => r.reminder_type === 'email').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'phone', name: p('dist_phone'), count: relances.filter(r => r.reminder_type === 'phone').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'letter', name: p('dist_letter'), count: relances.filter(r => r.reminder_type === 'letter').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterKey: 'sms', name: p('dist_sms'), count: relances.filter(r => r.reminder_type === 'sms').length, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
      ]
    },
    par_statut: {
      title: p('by_status_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: "bg-gradient-to-r from-orange-500 to-orange-600" },
        { filterKey: 'sent', name: p('dist_sent'), count: stats.sent, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: 'responded', name: p('dist_responded'), count: relances.filter(r => r.status === 'responded').length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'no_response', name: p('dist_no_response'), count: relances.filter(r => r.status === 'no_response').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
      ]
    }
  };

  const distributionInfo = relancesDistribution[mainViewMode] || relancesDistribution.all_reminders;

  const columnFilterRules = useMemo(() => [
    textColumnRule('reminder_number', (row) => row.reminder_number),
    textColumnRule('reminder_date', (row) => row.reminder_date),
    textColumnRule('client_name', (row) => row.client_name),
    textColumnRule('amount_due', (row) => String(row.amount_due ?? '')),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredRelances = useMemo(() => {
    const base = relances.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchTerm && !r.reminder_number?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Filter by view mode
    if (mainViewMode === 'automatiques' && !r.is_automatic) return false;
    if (mainViewMode === 'manuelles' && r.is_automatic) return false;
    
    if (mainViewMode === 'all_reminders') {
      if (quickFilter === 'pending' && r.status !== 'pending') return false;
      if (quickFilter === 'sent' && r.status !== 'sent') return false;
      if (quickFilter === 'responded' && r.status !== 'responded') return false;
      if (quickFilter === 'critical' && parseInt(r.days_overdue || 0) <= 30) return false;
    }
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_reminders':
          if (activeDistributionFilter === 'pending' && r.status !== 'pending') return false;
          if (activeDistributionFilter === 'sent' && r.status !== 'sent') return false;
          if (activeDistributionFilter === 'responded' && r.status !== 'responded') return false;
          if (activeDistributionFilter === 'overdue_30' && parseInt(r.days_overdue || 0) <= 30) return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'email' && r.reminder_type !== 'email') return false;
          if (activeDistributionFilter === 'phone' && r.reminder_type !== 'phone') return false;
          if (activeDistributionFilter === 'letter' && r.reminder_type !== 'letter') return false;
          if (activeDistributionFilter === 'sms' && r.reminder_type !== 'sms') return false;
          break;
        case 'automatiques':
          if (activeDistributionFilter === 'scheduled' && (!r.is_automatic || r.status !== 'pending')) return false;
          if (activeDistributionFilter === 'sent_auto' && (!r.is_automatic || r.status !== 'sent')) return false;
          if (activeDistributionFilter === 'failed' && (!r.is_automatic || r.status !== 'failed')) return false;
          if (activeDistributionFilter === 'total_auto' && !r.is_automatic) return false;
          break;
        case 'manuelles':
          if (activeDistributionFilter === 'to_send' && (r.is_automatic || r.status !== 'pending')) return false;
          if (activeDistributionFilter === 'sent_manual' && (r.is_automatic || r.status !== 'sent')) return false;
          if (activeDistributionFilter === 'responded_manual' && (r.is_automatic || r.status !== 'responded')) return false;
          if (activeDistributionFilter === 'total_manual' && r.is_automatic) return false;
          break;
        case 'archives':
          const date = new Date(r.reminder_date);
          const today = new Date();
          if (activeDistributionFilter === 'this_week') {
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            if (date < weekAgo) return false;
          }
          if (activeDistributionFilter === 'this_month' && date.getMonth() !== today.getMonth()) return false;
          if (activeDistributionFilter === 'this_year' && date.getFullYear() !== today.getFullYear()) return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'pending' && r.status !== 'pending') return false;
          if (activeDistributionFilter === 'sent' && r.status !== 'sent') return false;
          if (activeDistributionFilter === 'responded' && r.status !== 'responded') return false;
          if (activeDistributionFilter === 'no_response' && r.status !== 'no_response') return false;
          break;
      }
    }
    return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [relances, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, filters, columnFilterRules]);

  const relancesFooter = useMemo(() => {
    const list = filteredRelances;
    let montantDu = 0;
    const facSet = new Set();
    list.forEach((r) => {
      montantDu += parseFloat(r.amount_due) || 0;
      const k = String(r.invoice_number || r.invoice_id || '').trim();
      if (k) facSet.add(k);
    });
    return { montantDu, nbFacturesDistinctes: facSet.size };
  }, [filteredRelances]);


  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/facturation/relances/${id}`);
        loadRelances();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/facturation/relances/${id}`)));
        setSelectedRecords([]);
        loadRelances();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredRelances);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredRelances.map((r) => r.id), [filteredRelances]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const relanceTableColSpan = 2 + visibleRelanceTableCols.length;

  const toggleRelanceTableColumn = (key) => {
    setVisibleRelanceTableCols((prev) => {
      const order = RELANCE_TABLE_COLUMN_KEYS;
      if (prev.includes(key)) {
        const next = prev.filter((k) => k !== key);
        if (next.length === 0) return prev;
        try {
          localStorage.setItem(RELANCE_TABLE_VISIBLE_COLS_STORAGE, JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      }
      if (prev.length >= RELANCE_TABLE_COLUMNS_MAX) {
        alert(p('max_columns_alert', { max: RELANCE_TABLE_COLUMNS_MAX }));
        return prev;
      }
      const next = order.filter((k) => k === key || prev.includes(k));
      try {
        localStorage.setItem(RELANCE_TABLE_VISIBLE_COLS_STORAGE, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  // State for clickable status badge dropdown

  // Handle status change
  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/relances/${recordId}`, { status: newStatus });
      loadRelances();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getTypeBadge = (type) => {
    const config = typeOptions.find(t => t.value === type) || typeOptions[0];
    const { label, icon: Icon } = config;
    const colors = {
      email: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      phone: 'bg-green-500/20 text-green-400 border-green-500/30',
      letter: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      sms: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${colors[type]}`}><Icon size={14} />{label}</span>;
  };

  const getOverdueBadge = (days) => {
    const daysNum = parseInt(days || 0);
    const label = p('days_suffix', { count: daysNum });
    if (daysNum < 15) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{label}</span>;
    if (daysNum < 30) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-orange-500/20 text-orange-400 border-orange-500/30"><AlertTriangle size={14} />{label}</span>;
    return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-red-500/20 text-red-400 border-red-500/30"><AlertOctagon size={14} />{label}</span>;
  };

  const getAutoBadge = (isAuto) => {
    if (isAuto) return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-purple-500/20 text-purple-400 border-purple-500/30"><RefreshCcw size={14} />{p('mode_auto')}</span>;
    return <span className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border bg-blue-500/20 text-blue-400 border-blue-500/30"><Users size={14} />{p('mode_manual')}</span>;
  };

  const getPeriodBadge = (period) => {
    if (!period) return <span className="text-white/40 text-xs">-</span>;
    const colors = {
      'J+7': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'J+15': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'J+30': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      'J+45': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'J+60': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      'J+90': 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${colors[period] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}><Clock size={14} />{period}</span>;
  };

  const getRelanceTypeBadge = (type) => {
    if (!type) return <span className="text-white/40 text-xs">-</span>;
    const typeConfig = {
      reminder: { label: p('relance_type_reminder'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      formal_notice: { label: p('relance_type_formal'), color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      final_notice: { label: p('relance_type_final'), color: 'bg-red-500/20 text-red-400 border-red-500/30' },
      legal: { label: p('relance_type_legal'), color: 'bg-red-600/20 text-red-500 border-red-600/30' },
      friendly: { label: p('relance_type_friendly'), color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      urgent: { label: p('relance_type_urgent'), color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    };
    const config = typeConfig[type] || { label: getRelanceTypeLabel(type), color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${config.color}`}><AlertCircle size={14} />{config.label}</span>;
  };

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total'), value: stats.total, icon: Bell, color: 'from-red-500 to-red-600' },
    { key: 'automatic', title: p('stat_automatic'), value: stats.automatic, icon: RefreshCcw, color: 'from-purple-500 to-purple-600' },
    { key: 'manual', title: p('stat_manual'), value: stats.manual, icon: Users, color: 'from-blue-500 to-blue-600' },
    { key: 'pending', title: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-orange-500 to-orange-600' },
    { key: 'overdue', title: p('stat_overdue_30'), value: stats.overdue, icon: AlertOctagon, color: 'from-red-500 to-red-600', className: 'max-lg:hidden' },
  ], [stats, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_active') || p('status_values.active') || p('dist_active') },
    { value: 'inactive', label: p('status_inactive') || p('status_values.inactive') || p('dist_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? (p('status') || p('status_label') || p('col_status'))
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'reminder_number', label: p('col_reminder_number'), required: true },
    { key: 'reminder_date', label: p('col_date') },
    { key: 'client_name', label: p('col_client') },
    { key: 'invoice_number', label: p('col_invoice') },
    { key: 'amount_due', label: p('col_amount') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 5 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <>
      <FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-6"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Bell}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={null}
      createLabel={p('new_reminder')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
<div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_reminders' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
              {distributionInfo.data.map((item) => {
                const itemKey = item.filterKey || item.name;
                return (
                <button key={itemKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === itemKey ? null : itemKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === itemKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === itemKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === itemKey ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? p('percent_of_total', { percent: (item.count / total * 100).toFixed(1) }) : 0;
                  })()}</p>
                </button>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p('columns_title')}</h3>
              <p className="text-sm text-white/60 max-w-xl">
                {p('columns_hint')}{' '}
                <span className="text-white font-semibold">{RELANCE_TABLE_COLUMNS_MAX}</span>{' '}
                {p('columns_always_visible')}
                <span className="text-white/80">
                  {' '}
                  — {p('columns_active', { current: visibleRelanceTableCols.length, max: RELANCE_TABLE_COLUMNS_MAX })}
                </span>
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {relanceTableColumnOptions.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-white/25 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    className="rounded border-white/30 text-blue-500 focus:ring-blue-400 bg-white/10 shrink-0"
                    checked={visibleRelanceTableCols.includes(key)}
                    onChange={() => toggleRelanceTableColumn(key)}
                  />
                  <span className="text-white text-sm font-medium">{label}</span>
                </label>
              ))}
            </div>
          </motion.div>
        </div>
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                isLight={isDashboardLightMode}
              />
              
              {selectedRecords.length > 0 && (
                <button onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium">
                  <Trash2 size={16} /><span className="hidden sm:inline">{p('delete_count', { count: selectedRecords.length })}</span>
                </button>
              )}
              
              {/* Status Filter */}
              <div className="relative" ref={statusFilterRef}>
                <button 
                  onClick={() => { setIsStatusFilterOpen(!isStatusFilterOpen); setIsDateRangeOpen(false); setShowImportDropdown(false); setShowExportDropdown(false); }} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Filter size={18} className="text-white/80" />
                  <span className="text-white font-medium text-sm">{p('status_label')}</span>
                  <ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', 
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <Filter size={16} className="text-white/50" />
                        <span className="text-sm font-medium">{p('all_statuses')}</span>
                      </button>
                      {statusOptions.map((option) => {
                        const OptionIcon = option.icon;
                        return (
                      <button
                        key={option.value}
                        onClick={() => {
                          setStatusFilter(option.value);
                          setIsStatusFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === option.value ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <OptionIcon size={16} className={option.style.split(' ')[1].replace('text-', 'text-')} />
                        <span className="text-sm font-medium">{option.label}</span>
                      </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Date Range Filter */}
              <div className="relative" ref={dateRangeRef}>
                <button 
                  onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setIsStatusFilterOpen(false); setShowImportDropdown(false); setShowExportDropdown(false); }} 
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Calendar size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('period_label')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('date_start')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({...dateRange, start: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select_placeholder')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('date_end')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({...dateRange, end: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={p('select_placeholder')}
                              locale={datePickerLocale}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-white/10">
                          <button
                            onClick={() => {
                              setDateRange({ start: null, end: null });
                              setIsDateRangeOpen(false);
                            }}
                            className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl text-white/80 text-sm font-medium transition-all"
                          >
                            {p('reset')}
                          </button>
                          <button
                            onClick={() => setIsDateRangeOpen(false)}
                            className="flex-1 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-xl text-blue-300 text-sm font-medium transition-all"
                          >
                            {p('apply')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Upload size={16} /><span className="text-sm font-medium">{p('import_label')}</span><ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={() => { handleImportCSV(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={() => { handleImportExcel(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                      <button onClick={() => { handleImportJSON(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-blue-400" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={() => { handleImportXML(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={() => { handleImportTXT(); setShowImportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">TXT/TSV</span></button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Download size={16} /><span className="text-sm font-medium">{p('export_label')}</span><ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-2">
                      <button onClick={() => { handleExportPDF(); setShowExportDropdown(false); }} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? p('generating_pdf') : t('common.format_pdf')}</span></button>
                      <button onClick={() => { handleExportExcel(); setShowExportDropdown(false); }} disabled={isGeneratingExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{isGeneratingExcel ? 'Excel...' : t('common.format_excel')}</span></button>
                      <button onClick={() => { handleExportCSV(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                      <button onClick={() => { handleExportJSON(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-blue-300" /><span className="text-sm font-medium">JSON</span></button>
                      <button onClick={() => { handleExportXML(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                      <button onClick={() => { handleExportHTML(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">HTML</span></button>
                      <button onClick={() => { handleExportTXT(); setShowExportDropdown(false); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('reminder_number') ? (
                  <TableColumnHeaderFilter columnKey="reminder_number" label={p('col_reminder_number')} value={filters.reminder_number || ''} onChange={(v) => setFilter('reminder_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  ) : null}
                  {showCol('reminder_date') ? (
                  <TableColumnHeaderFilter columnKey="reminder_date" label={p('col_date')} value={filters.reminder_date || ''} onChange={(v) => setFilter('reminder_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  ) : null}
                  {showCol('client_name') ? (
                  <TableColumnHeaderFilter columnKey="client_name" label={p('col_client')} value={filters.client_name || ''} onChange={(v) => setFilter('client_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" />
                  ) : null}
                  {showCol('invoice_number') ? (
                  <th className="px-6 py-4 text-left text-white font-semibold">{p('col_invoice')}</th>
                  ) : null}
                  {showCol('amount_due') ? (
                  <TableColumnHeaderFilter columnKey="amount_due" label={p('col_amount')} value={filters.amount_due || ''} onChange={(v) => setFilter('amount_due', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" />
                  ) : null}
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_overdue')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_period')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_mode')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_reminder_type')}</th>
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_channel')}</th>
                  {showCol('status') ? (
                  <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((o) => ({ value: o.value, label: o.label }))} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredRelances.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      {showCol('reminder_number') ? (
                      <td className="px-6 py-4">
                        <RelanceEditableCell recordId={record.id} field="reminder_number" value={record.reminder_number} className="text-white font-semibold"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'reminder_number'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                        />
                      </td>
                      ) : null}
                      {showCol('reminder_date') ? (
                      <td className="px-6 py-4">
                        <RelanceEditableCell recordId={record.id} field="reminder_date" value={record.reminder_date} className="text-white/70"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'reminder_date'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                        />
                      </td>
                      ) : null}
                      {showCol('client_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-0.5">
                          <RelanceEditableCell recordId={record.id} field="client_name" value={record.client_name} className="text-white/90"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_name'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                          />
                          <RelanceEditableCell recordId={record.id} field="client_email" value={record.client_email} className="text-white/50 text-xs"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_email'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                          />
                        </div>
                      </td>
                      ) : null}
                      {showCol('invoice_number') ? (
                      <td className="px-6 py-4">
                        <RelanceEditableCell recordId={record.id} field="invoice_number" value={record.invoice_number} className="text-white/70"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'invoice_number'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                        />
                      </td>
                      ) : null}
                      {showCol('amount_due') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex flex-wrap items-center justify-center gap-1">
                          <RelanceEditableCell recordId={record.id} field="amount_due" value={record.amount_due !== undefined && record.amount_due !== null && record.amount_due !== '' ? String(record.amount_due) : ''} className="text-white font-semibold"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'amount_due'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                          />
                          {!(editingCell?.recordId === record.id && editingCell?.field === 'amount_due') && (
                            <span className="text-white/50 text-xs">{getBrandCurrencySuffix()}</span>
                          )}
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getOverdueBadge(record.days_overdue)}</div></td>
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getPeriodBadge(record.period)}</div></td>
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getAutoBadge(record.is_automatic)}</div></td>
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getRelanceTypeBadge(record.type)}</div></td>
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getTypeBadge(record.reminder_type)}</div></td>
                      {showCol('status') ? (
                      <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view') || p('view') || t('common.view')}
                          deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><Bell size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredRelances.length })}</span>
                      <span>{p('footer_invoices')}: <span className="text-white font-semibold">{relancesFooter.nbFacturesDistinctes}</span></span>
                      {stats.unpaidInvoices > 0 && (
                        <span>{p('footer_unpaid_dossiers', { count: stats.unpaidInvoices, defaultValue: '{{count}} impayés' })}</span>
                      )}
                      <span>{p('footer_amount_due')}: <span className="text-white font-semibold">{formatBrandMoney(relancesFooter.montantDu)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredRelances.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-y-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2 min-w-0">
                          <Bell size={20} className="text-blue-400 shrink-0" />
                          <RelanceEditableCell recordId={record.id} field="reminder_number" value={record.reminder_number} className="text-xl font-bold text-white break-words"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'reminder_number'}
                            editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                          />
                        </h3>
                        <div className="flex gap-2 flex-wrap">{getAutoBadge(record.is_automatic)}<QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />{getRelanceTypeBadge(record.type)}{getPeriodBadge(record.period)}{getTypeBadge(record.reminder_type)}{getOverdueBadge(record.days_overdue)}</div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_client')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_name')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="client_name" value={record.client_name} className="text-white/90 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_name'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                            <div><span className="font-medium">{p('card_email')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="client_email" value={record.client_email} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_email'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                            <div><span className="font-medium">{p('card_phone')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="client_phone" value={record.client_phone} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_phone'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_invoice')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_invoice_no')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="invoice_number" value={record.invoice_number} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'invoice_number'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                            <div><span className="font-medium">{p('card_invoice_date')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="invoice_date" value={record.invoice_date} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'invoice_date'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                            <div><span className="font-medium">{p('card_amount_due')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="amount_due" value={record.amount_due !== undefined && record.amount_due !== null && record.amount_due !== '' ? String(record.amount_due) : ''} className="text-white inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'amount_due'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                              {!(editingCell?.recordId === record.id && editingCell?.field === 'amount_due') && <span className="text-white/50"> {getBrandCurrencySuffix()}</span>}
                            </div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_followup')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_reminder_date')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="reminder_date" value={record.reminder_date} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'reminder_date'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                            <div><span className="font-medium">{p('card_sent')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="sent_date" value={record.sent_date} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'sent_date'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                            <div><span className="font-medium">{p('card_followup_date')}:</span>{' '}
                              <RelanceEditableCell recordId={record.id} field="followup_date" value={record.followup_date} className="text-white/85 inline"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'followup_date'}
                                editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('action_view') || p('view') || t('common.view')}
                      deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Bell size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('view_title')}
          icon={Bell}
          maxWidth="1100px"
          testId="relance-view-modal"
        >
              <div className="space-y-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Bell size={18} className="text-blue-400" />{p('section_reminder_info')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('col_reminder_number')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="reminder_number" value={viewingRecord.reminder_number} className="text-white font-medium"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'reminder_number'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                    /></div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_date')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="reminder_date" value={viewingRecord.reminder_date} className="text-white font-medium"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'reminder_date'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                    /></div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_period')}</p><div className="mt-1">{getPeriodBadge(viewingRecord.period)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_reminder_type')}</p><div className="mt-1">{getRelanceTypeBadge(viewingRecord.type)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_mode')}</p><div className="mt-1">{getAutoBadge(viewingRecord.is_automatic)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_status')}</p><div className="mt-1">{getStatusBadge(viewingRecord.status)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_channel')}</p><div className="mt-1">{getTypeBadge(viewingRecord.reminder_type)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_overdue')}</p><div className="mt-1">{getOverdueBadge(viewingRecord.days_overdue)}</div></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('card_amount_due')}</p><div className="inline-flex items-center gap-1 text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="amount_due" value={viewingRecord.amount_due !== undefined && viewingRecord.amount_due !== null && viewingRecord.amount_due !== '' ? String(viewingRecord.amount_due) : ''} className="text-white font-medium"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'amount_due'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                    />
                    {!(editingCell?.recordId === viewingRecord.id && editingCell?.field === 'amount_due') && <span className="text-white/60 text-sm">{getBrandCurrencySuffix()}</span>}</div></div>
                    {viewingRecord.is_automatic && (
                      <div><p className="text-white/60 text-sm mb-1">{p('label_recurrence')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="recurrence" value={viewingRecord.recurrence} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'recurrence'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_client_invoice')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('col_client')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="client_name" value={viewingRecord.client_name} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'client_name'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('card_email')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="client_email" value={viewingRecord.client_email} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'client_email'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_phone')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="client_phone" value={viewingRecord.client_phone} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'client_phone'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_invoice_no')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="invoice_number" value={viewingRecord.invoice_number} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'invoice_number'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_invoice_date')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="invoice_date" value={viewingRecord.invoice_date} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'invoice_date'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                    </div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_followup')}</h3>
                    <div className="space-y-3">
                      <div><p className="text-white/60 text-sm mb-1">{p('label_sent_date')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="sent_date" value={viewingRecord.sent_date} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'sent_date'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_response_date')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="response_date" value={viewingRecord.response_date} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'response_date'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                      <div><p className="text-white/60 text-sm mb-1">{p('label_next_followup')}</p><div className="text-white font-medium"><RelanceEditableCell recordId={viewingRecord.id} field="followup_date" value={viewingRecord.followup_date} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'followup_date'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                      /></div></div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_notes')}</h3>
                    <div className="text-white/70"><RelanceEditableCell recordId={viewingRecord.id} field="notes" value={viewingRecord.notes} className="text-white/70"
                      isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'notes'}
                      editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={clickToEditTitle}
                    /></div>
                  </div>
              </div>
        </QawaemErpViewModal>
      ) : null}

    </>
  );
};

export default RelancesPage;


